import { useRef, type KeyboardEvent } from 'react'

import Tooltip from '@/components/ui/Tooltip'
import { TOUCH_TARGET_Y } from '@/components/ui/button-variants'
import { useSlidingIndicator } from '@/components/ui/use-sliding-indicator'
import { cn } from '@/lib/utils'

export interface SegmentOption<T extends string> {
  value: T
  label: string
  /** The label's language when it is not English — `ne` for नेपाली — so a screen reader voices
   *  it in that language and `:lang(ne)` sets it in the Devanagari face. */
  lang?: string
  /** This one choice is unavailable: drawn at the disabled opacity and skipped by the arrows. */
  disabled?: boolean
  /**
   * Why a disabled option is unavailable ("Not available yet", rest:al): the segment sits in a
   * focusable span carrying a chip tooltip, since a disabled button hears no pointer or focus.
   * Only meaningful with `disabled`.
   */
  tip?: string
}

/** Every arrow moves the check one step, wrapping at the ends (the WAI-ARIA radio group). */
const ARROW_STEPS: Readonly<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
}

interface RadioSegmentsProps<T extends string> {
  options: readonly SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  /** The group's own box. */
  className: string
  /** Each segment's classes, checked or not. The checked one draws no box of its own: the thumb
   *  does, and slides to it. */
  segmentClassName: (checked: boolean) => string
  /** The sliding thumb's skin: its fill and hairline. `null` draws no thumb: the checked segment
   *  paints its own box (SegmentedControl's `flat` skin). */
  thumbClassName: string | null
  /** How long the thumb takes to move: `slow` 480ms (a period or segment row), `med` 220ms
   *  (the Line / Candles toggle, which the reference moves faster), `fast` 180ms (Mitra's
   *  language toggle). Never under reduced motion. */
  thumbSpeed?: 'slow' | 'med' | 'fast'
  /** The whole group is unavailable — every segment a disabled button, out of the Tab order. */
  disabled?: boolean
}

/**
 * A row of segments that is one radio group — the engine SegmentedControl and PeriodTabs share,
 * so the two differ in skin only.
 *
 * `radiogroup`, not `tablist`: picking Candles, a date range or a period sets a value the view
 * below is drawn from; it does not reveal a different panel. A radio group also says what the
 * member can do with it — "radio button, 2 of 2, checked" — where a tab promises a panel that
 * is not there.
 *
 * One Tab stop for the whole group, on the checked segment (the first enabled one when `value`
 * matches none or names a disabled option, so the group is never skipped). Every arrow key moves
 * the check and the focus together, wrapping at the ends and stepping over disabled segments, and
 * Home/End jump to the first and last enabled ones, as the app's other roving groups do; Space
 * and Enter check the focused segment through the button's own click.
 *
 * Disabled is a real `disabled` on the button rather than `pointer-events-none` on the group: the
 * latter still let a keyboard check a segment, and still announced it as available.
 *
 * The checked segment's box is one `thumb` behind the labels, measured off the checked button and
 * slid there (transform + width, 480ms ease-out) rather than drawn per segment, so a change of
 * check reads as the same piece moving. The label wrapper holds the bold width in reserve, so a
 * 600 selection never widens its segment.
 */
export function RadioSegments<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  segmentClassName,
  thumbClassName,
  thumbSpeed = 'slow',
  disabled = false,
}: RadioSegmentsProps<T>) {
  const { containerRef, box } = useSlidingIndicator<HTMLDivElement>(value)
  const segments = useRef(new Map<T, HTMLButtonElement>())
  const checkedIndex = options.findIndex((option) => option.value === value)
  const isEnabled = (index: number) => !disabled && options[index]?.disabled !== true
  const firstEnabled = options.findIndex((_, index) => isEnabled(index))
  const tabStop = checkedIndex >= 0 && isEnabled(checkedIndex) ? checkedIndex : firstEnabled

  const check = (index: number) => {
    const option = options[index]
    if (option === undefined) return
    if (option.value !== value) onChange(option.value)
    segments.current.get(option.value)?.focus()
  }

  // Moves from the FOCUSED segment, not the checked one: they differ while `value` matches no
  // option and focus sits on the fallback stop.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const from = options.findIndex((option) => segments.current.get(option.value) === event.target)
    if (from < 0) return

    // Home walks forward from before the first segment and End backward from past the last, so
    // all three keys share the one skip-the-disabled walk.
    const step = ARROW_STEPS[event.key]
    let start: number
    let direction: number
    if (step !== undefined) [start, direction] = [from, step]
    else if (event.key === 'Home') [start, direction] = [-1, 1]
    else if (event.key === 'End') [start, direction] = [options.length, -1]
    else return

    event.preventDefault()
    for (let hop = 1; hop <= options.length; hop += 1) {
      const to = (((start + direction * hop) % options.length) + options.length) % options.length
      if (isEnabled(to)) {
        check(to)
        return
      }
    }
  }

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      onKeyDown={onKeyDown}
      className={cn('relative', className)}
    >
      {box === null || thumbClassName === null ? null : (
        <span
          aria-hidden="true"
          className={cn(
            // `width` stays animated here: the thumb is a bordered, filled box, which a scaleX
            // would stretch. The dim follows the labels' own disabled opacity.
            'pointer-events-none absolute top-0 left-0 border',
            disabled && 'opacity-38',
            box.animate &&
              (thumbSpeed === 'fast'
                ? 'transition-[transform,width] duration-180 ease-out motion-reduce:transition-none'
                : thumbSpeed === 'med'
                  ? 'transition-[transform,width] duration-med ease-out'
                  : 'transition-[transform,width] duration-slow ease-out'),
            thumbClassName,
          )}
          style={{
            top: box.top,
            width: box.width,
            height: box.height,
            transform: `translateX(${String(box.left)}px)`,
          }}
        />
      )}
      {options.map((option, index) => {
        const checked = index === checkedIndex
        const segment = (
          <button
            key={option.value}
            ref={(element) => {
              if (element === null) segments.current.delete(option.value)
              else segments.current.set(option.value, element)
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={index === tabStop ? 0 : -1}
            lang={option.lang}
            disabled={!isEnabled(index)}
            onClick={() => {
              if (!checked) onChange(option.value)
            }}
            className={cn(
              segmentClassName(checked),
              'disabled:pointer-events-none disabled:opacity-38',
            )}
          >
            <span data-text={option.label} className="reserve-bold">
              {option.label}
            </span>
          </button>
        )
        if (option.tip === undefined || isEnabled(index)) return segment
        return (
          <Tooltip key={option.value} variant="chip" side="above" align="center" title={option.tip}>
            <span
              tabIndex={0}
              role="group"
              aria-label={`${option.label}, ${option.tip}`}
              className="flex rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control *:w-full"
            >
              {segment}
            </span>
          </Tooltip>
        )
      })}
    </div>
  )
}

/**
 * Figma draws the control 32–38px tall; `md` builds the 38 as 40 on the 4-grid (Line/Candles
 * `4236:100828`, alert type `4243:89064`). `sm` is the pages revamp's one segmented row
 * (2026-10-27): a 36px track — the toolbar height, so it stands level with the controls and inner
 * tabs beside it — holding 28px segments of 12px labels. The track's 4px padding is what sits
 * around the checked segment, so a segment is the track less 8px. 8px either side of the widest
 * label is what puts Line / Candles at the frame's 144px. Below `lg` each segment's target grows
 * vertically to 44px (`TOUCH_TARGET_Y`).
 */
const SIZES = {
  /** The form-field height (`h-11`, the Field's), so a control sits level with an input. */
  lg: { track: 'h-11', segment: 'h-9 px-4 text-body-m', checked: 'text-h-s' },
  md: { track: 'h-10', segment: 'h-8 px-2 text-body-m', checked: 'text-h-s' },
  sm: { track: 'h-9', segment: 'h-7 px-2 text-body-xs', checked: 'text-h-xs' },
} as const

/**
 * The control's look (Market parity, 2026-10-28). `thumb` is the default: a `bg-popover` thumb with
 * a strong hairline slides under the checked label. `quiet` is the mock's `mka-seg` (Gainers &
 * Losers' 1 day / 1 week): every label 12/600, the idle ones in the tertiary ink, over a borderless
 * raised thumb that slides at the med step. `flat` is its heatmap sizing row: no thumb, the checked
 * segment on the card fill with a subtle hairline, labels set from the left. `inset` is the Broker
 * Analysis period row (`bra-seg`, br:kit): the inset track with no edge, segments as wide as their
 * own labels with 12 either side, idle labels secondary brightening to white, and the checked one
 * on the raised fill inside a strong hairline in 600 white — swapped in place, no thumb sliding.
 * `solid` (rest:ub) is the sign-up Phone / Email switch (`ob-seg`): a round track, the checked
 * segment a white pill that slides, its label black at 600; idle words in the tertiary ink going to
 * white on hover.
 */
type SegmentSkin = 'thumb' | 'quiet' | 'flat' | 'inset' | 'solid'

const SKINS: Record<
  SegmentSkin,
  {
    thumb: string | null
    track: string
    segment: string
    checked: string
    idle: string
  }
> = {
  thumb: {
    thumb: 'border-border-strong bg-popover',
    track: '',
    segment: 'justify-center',
    checked: 'text-foreground',
    idle: 'text-muted-foreground hover:text-foreground focus-visible:text-foreground',
  },
  quiet: {
    thumb: 'border-transparent bg-popover',
    track: 'gap-1',
    segment: 'justify-center px-4 text-h-xs',
    checked: 'text-foreground',
    idle: 'text-muted-foreground-subtle hover:text-muted-foreground focus-visible:text-muted-foreground',
  },
  flat: {
    thumb: null,
    track: '',
    segment: 'justify-start px-3',
    checked: 'border-border-subtle bg-card text-foreground',
    idle: 'text-muted-foreground-subtle hover:text-foreground focus-visible:text-foreground',
  },
  solid: {
    thumb: 'rounded-full border-transparent bg-foreground',
    track: 'rounded-full',
    segment: 'justify-center rounded-full',
    checked: 'text-background',
    idle: 'text-muted-foreground-subtle hover:text-foreground focus-visible:text-foreground',
  },
  inset: {
    thumb: null,
    // Each column sizes to its own label (`auto`), where the other skins share the widest one.
    track: 'auto-cols-auto',
    segment: 'justify-center px-3',
    checked: 'border-border-strong bg-popover text-foreground',
    idle: 'text-muted-foreground hover:text-foreground focus-visible:text-foreground',
  },
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  size?: keyof typeof SIZES
  /** See `SegmentSkin`. Default `thumb`. */
  skin?: SegmentSkin
  /** See `RadioSegmentsProps.thumbSpeed`. Default `slow`, `med` on the `quiet` skin. */
  thumbSpeed?: 'slow' | 'med'
  /** Stretch the track to its container, as the date picker's One day / Date range does
   *  (`5036:98065`). Segments stay equal either way. */
  fill?: boolean
  /** The whole control is unavailable, e.g. while the form it sits in submits. A single choice
   *  is disabled on its option instead. */
  disabled?: boolean
  className?: string | undefined
}

/**
 * One setting, picked from two to four fixed options: Line / Candles, Price / Volume,
 * One day / Date range, English / नेपाली.
 *
 * A `bg-muted` track with the checked segment lifted onto it — a sliding `bg-popover` thumb with
 * a `border-border-strong` hairline, a 600 label — and the others as muted words that brighten on
 * hover. Every segment is as wide as the widest (`auto-cols-fr` on a grid, which sizes each
 * column to the largest label), so the control does not change shape as the check moves.
 *
 * Not SegmentedBar: that one is a `role="img"` proportional bar, not a control.
 */
export default function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'md',
  skin = 'thumb',
  thumbSpeed = skin === 'quiet' ? 'med' : 'slow',
  fill = false,
  disabled = false,
  className,
}: SegmentedControlProps<T>) {
  return (
    <RadioSegments
      options={options}
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      disabled={disabled}
      thumbSpeed={thumbSpeed}
      thumbClassName={SKINS[skin].thumb}
      className={cn(
        'auto-cols-fr grid-flow-col bg-muted p-1',
        fill ? 'grid w-full' : 'inline-grid',
        SIZES[size].track,
        SKINS[skin].track,
        className,
      )}
      segmentClassName={(checked) =>
        cn(
          'relative inline-flex items-center border border-transparent whitespace-nowrap transition-colors',
          TOUCH_TARGET_Y,
          SIZES[size].segment,
          SKINS[skin].segment,
          checked ? [SIZES[size].checked, SKINS[skin].checked] : SKINS[skin].idle,
        )
      }
    />
  )
}
